package com.tregz.tremenz.ui.page

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.ui.semantics.Role
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.tregz.tremenz.ui.component.Title
import com.tregz.tremenz.ui.component.EmptyState
import com.tregz.tremenz.ui.label.LabelKey
import com.tregz.tremenz.ui.label.labelText
import com.tregz.tremenz.ui.media.MediaImage

data class PagePhotoVenue(
    val title: String,
    val subtitle: String,
    val year: String,
    val events: String,
    val location: String,
    val logoUrl: String,
    val webUrl: String,
    val photos: List<String>
)

@Composable
fun pagePhotoVenues(): List<PagePhotoVenue> = listOf(
    PagePhotoVenue(
        title = "La Maison de Débauche",
        subtitle = labelText(LabelKey.PHOTO_VENUE_1_SUBTITLE),
        year = "2016",
        events = labelText(LabelKey.PHOTO_VENUE_EVENTS),
        location = "Trois-Rivières (Canada)",
        logoUrl = "assets/sm_debauche.webp",
        webUrl = "https://www.lecarlito.com/",
        photos = listOf(
            "assets/photo/md_2016_01.webp",
            "assets/photo/md_2016_02.webp",
            "assets/photo/md_2016_03.webp",
            "assets/photo/md_2016_04.webp",
            "assets/photo/md_2016_05.webp",
            "assets/photo/md_2016_06.webp"
        )
    ),
    PagePhotoVenue(
        title = "La Grange Taverne Festive",
        subtitle = labelText(LabelKey.PHOTO_VENUE_2_SUBTITLE),
        year = "2015",
        events = labelText(LabelKey.PHOTO_VENUE_EVENTS),
        location = "Trois-Rivières (Canada)",
        logoUrl = "assets/sm_grange.webp",
        webUrl = "https://www.facebook.com/BarInfidele",
        photos = listOf(
            "assets/photo/gr_2015_01.webp",
            "assets/photo/gr_2015_02.webp",
            "assets/photo/gr_2015_03.webp",
            "assets/photo/gr_2015_04.webp",
            "assets/photo/gr_2015_05.webp",
            "assets/photo/gr_2015_06.webp"
        )
    )
)

/**
 * Web exclusive gallery.
 *
 * Displays residency venues with their logo, website link, and photo gallery.
 */
@Composable
fun PagePhotos(
    venues: List<PagePhotoVenue> = pagePhotoVenues(),
    onOpenLink: (String) -> Unit = {},
    modifier: Modifier = Modifier
) {
    val scheme = MaterialTheme.colorScheme
    var selectedPhoto by rememberSaveable { mutableStateOf<String?>(null) }

    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(bottom = 24.dp)
    ) {
        item {
            Title(
                title = labelText(LabelKey.ROUTE_PHOTOS),
                subtitle = labelText(LabelKey.HEADING_PHOTOS_SUBTITLE)
            )
        }

        if (venues.isEmpty()) {
            item {
                EmptyState(message = labelText(LabelKey.EMPTY_PHOTOS))
            }
        } else {
            items(venues) { venue ->
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    colors = CardDefaults.cardColors(containerColor = scheme.surface),
                    shape = MaterialTheme.shapes.medium
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier
                                    .weight(1f)
                                    .clip(RoundedCornerShape(6.dp))
                                    .clickable { onOpenLink(venue.webUrl) }
                            ) {
                                MediaImage(
                                    url = venue.logoUrl,
                                    contentDescription = labelText(LabelKey.PHOTO_LOGO).replace("%s", venue.title),
                                    modifier = Modifier
                                        .size(44.dp)
                                        .clip(RoundedCornerShape(8.dp))
                                        .background(scheme.surfaceVariant),
                                    contentScale = ContentScale.Fit
                                )
                                Spacer(modifier = Modifier.width(12.dp))
                                Column {
                                    Text(
                                        text = venue.title,
                                        color = scheme.onSurface,
                                        style = MaterialTheme.typography.titleMedium
                                    )
                                    Text(
                                        text = venue.subtitle,
                                        color = scheme.primary,
                                        style = MaterialTheme.typography.bodySmall,
                                        fontWeight = FontWeight.Medium
                                    )
                                }
                            }
                            Text(
                                text = "#${venue.year}",
                                color = scheme.primary,
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.SemiBold,
                                modifier = Modifier.padding(start = 8.dp)
                            )
                        }

                        Spacer(modifier = Modifier.height(8.dp))
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(
                                text = venue.events,
                                color = scheme.onSurfaceVariant,
                                style = MaterialTheme.typography.bodySmall
                            )
                            Text(
                                text = venue.location,
                                color = scheme.onSurfaceVariant,
                                style = MaterialTheme.typography.bodySmall
                            )
                        }

                        Spacer(modifier = Modifier.height(12.dp))
                        HorizontalDivider(color = scheme.outline)
                        Spacer(modifier = Modifier.height(12.dp))

                        BoxWithConstraints {
                            // Squares stay around 140 dp however wide the page is: 3 columns on a phone, up to 6.
                            val columns = (maxWidth / 140.dp).toInt().coerceIn(3, 6)
                            val photoRows = remember(venue.photos, columns) { venue.photos.chunked(columns) }
                            Column {
                                photoRows.forEachIndexed { index, row ->
                                    if (index > 0) Spacer(modifier = Modifier.height(8.dp))
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        row.forEach { photoUrl ->
                                            MediaImage(
                                                url = photoUrl,
                                                contentDescription = "${venue.title} photo",
                                                modifier = Modifier
                                                    .weight(1f)
                                                    .aspectRatio(1f)
                                                    .clip(RoundedCornerShape(8.dp))
                                                    .background(scheme.surfaceVariant)
                                                    .clickable(role = Role.Button) { selectedPhoto = photoUrl },
                                                contentScale = ContentScale.Crop
                                            )
                                        }
                                        repeat(columns - row.size) {
                                            Row(modifier = Modifier.weight(1f)) {}
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

        }
    }

    if (selectedPhoto != null) {
        AlertDialog(
            onDismissRequest = { selectedPhoto = null },
            confirmButton = {
                TextButton(onClick = { selectedPhoto = null }) {
                    Text(labelText(LabelKey.ACTION_CLOSE), color = scheme.primary)
                }
            },
            text = {
                MediaImage(
                    url = selectedPhoto,
                    contentDescription = null,
                    modifier = Modifier
                        .fillMaxWidth()
                        // Short windows would otherwise push the Close button off screen.
                        .heightIn(max = 360.dp)
                        .aspectRatio(4f / 3f)
                        .clip(RoundedCornerShape(8.dp)),
                    contentScale = ContentScale.Fit
                )
            }
        )
    }
}
